/**
 * Where an invitation link lands: /activate?token=...
 *
 * The invitee sets a password and is signed straight in. Their name is
 * pre-filled but editable, because whoever invited them may well have misspelled
 * it and they should not have to live with that.
 */
import { useState, type FormEvent } from 'react';
import { ApiError, activateAccount } from '../lib/api.js';
import { c, primaryButton } from '../theme.js';
import { AuthLayout, ErrorBox, Field } from './AuthLayout.js';

interface Props {
  token: string;
  /** Clears the token from the URL and enters the app. */
  onActivated: () => void;
}

export function ActivateScreen({ token, onActivated }: Props) {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Those passwords do not match');
      return;
    }

    setBusy(true);
    try {
      await activateAccount(token, password, name.trim() || undefined);
      onActivated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      heading="Set your password"
      intro="You’ve been added to a team. Choose a password and you’re in."
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field
          id="name"
          labelText="Your name"
          placeholder="Leave blank to keep what your manager entered"
          value={name}
          onChange={setName}
          autoComplete="name"
          autoFocus
        />
        <Field
          id="password"
          labelText="Password"
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          hint="At least 10 characters. Length beats symbols."
        />
        <Field
          id="confirm"
          labelText="Confirm password"
          type="password"
          placeholder="••••••••"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          required
        />

        {error && <ErrorBox>{error}</ErrorBox>}

        <button
          type="submit"
          disabled={busy}
          style={{ ...primaryButton, marginTop: 4, opacity: busy ? 0.65 : 1 }}
        >
          {busy ? 'Setting up…' : 'Set my password and continue'}
        </button>

        <p
          style={{
            fontSize: 12,
            color: c.faint,
            textAlign: 'center',
            margin: '2px 0 0',
            lineHeight: 1.7,
          }}
        >
          Invitations last 7 days and work once.
        </p>
      </form>
    </AuthLayout>
  );
}
